import LabelledSwitch from '../../core/LabelledSwitch';
import CommonIriDisplay from '../CommonIriDisplay';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Notification from '../../core/Notification';
import Errored from '../../core/Errored';
import { makeCancelable } from '../../core/utils/utils';
import AreYouSure from '../../core/AreYouSure';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import BasicLanguageFieldDisplay from '../../core/BasicLanguageFieldDisplay';
import { v4 as uuidv4 } from 'uuid';
import GS from '../../grafsync/grafsync';
import FindPropertyDialog from './FindPropertyDialog';
import TitledPanel from '../../core/TitledPanel';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles as styles } from '../../core/style/gstheme';
import { BN_NAMESPACE, MAX_TITLE_LENGTH, NS_GS } from '../../core/utils/constants';
import withGS from '../../core/withGShoc';
import SelectResourcesDialog from './SelectResourcesDialog';
import ShaclProperty from './ShaclProperty';
import { Typography } from '@material-ui/core';

const STANDARD_TARGETS = [
    "http://www.w3.org/2008/05/skos-xl#Label",
    "http://www.w3.org/2004/02/skos/core#Concept",
    "http://www.w3.org/2004/02/skos/core#ConceptScheme",
    "http://www.w3.org/2004/02/skos/core#Collection",
    "http://www.w3.org/2004/02/skos/core#OrderedCollection",
    "https://grafsync.graphifi.com/schema/gs/Dataset",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement",
    "https://graphifi.com/schema/xkos/Note"
];

class TemplateManager extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            areYouSure: false,
            associatedProperty: undefined,
            uniqueDomainList: [],
            inverses: [],
            advancedDomains: [],
            scroll: false,
            displayTemplateIRI: ""
        };
        this.cardRef = React.createRef();
        this.cancellable = null;
    }

    componentDidMount() {
        this.collectTemplateData();
    }

    static getDerivedStateFromProps(props, state) {
        if (props.data._version !== state._version) {
            return {
                associatedProperty: undefined,
                isLoading: false,
                _version: props.data._version,
                displayTemplateIRI: props.displayTemplateIRI,
                ...(props.displayTemplateIRI !== state.displayTemplateIRI && props.displayTemplateIRI === props.id) && {
                    scroll: true
                }
            };
        }
        if (props.displayTemplateIRI === props.id && props.displayTemplateIRI !== state.displayTemplateIRI) {
            return {
                displayTemplateIRI: props.displayTemplateIRI,
                scroll: true
            };
        }
        if (props.displayTemplateIRI !== state.displayTemplateIRI) {
            return {
                displayTemplateIRI: props.displayTemplateIRI
            };
        }

        return null;
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.associatedProperty === undefined) {
            this.collectTemplateData();
        }
        if (this.state.scroll && this.cardRef && this.cardRef.current && !this.props.data.pendingDeletion) {
            this.cardRef.current.scrollIntoView({
                block: 'start',
                inline: 'center',
            });
            this.setState({ scroll: false });
        }
    }

    loadData(iris, options) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, false));
        return this.cancellable;
    }

    loadPathData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph, false));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    collectTemplateData() {
        if (this.state.isLoading) return;
        const { data } = this.props;
        if (!data.shProperty) return;
        this.setState({ isLoading: true }, async () => {
            try {
                await this.loadData(data.shProperty).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                let pathList = [];
                data.shProperty.forEach(shProp => {
                    if (GS.isLoaded(shProp, this.props.graph)) {
                        const shPropData = GS.data(shProp, this.props.graph);
                        if (shPropData.shPath && !pathList.includes(shPropData.shPath)) {
                            pathList.push(shPropData.shPath);
                        }
                    }
                });
                const associatedProjectGraph = this.props.ontologyProjectGraph;
                const inverseList = GS.getInverses(associatedProjectGraph);
                await this.loadPathData(pathList, associatedProjectGraph).promise;
                pathList.sort((a, b) => {
                    const aData = GS.isLoaded(a, associatedProjectGraph) ? GS.data(a, associatedProjectGraph) : null;
                    const bData = GS.isLoaded(b, associatedProjectGraph) ? GS.data(b, associatedProjectGraph) : null;
                    const labelA = aData && aData.label[this.props.defaultLang] ? aData.label[this.props.defaultLang] : a;
                    const labelB = bData && bData.label[this.props.defaultLang] ? bData.label[this.props.defaultLang] : b;
                    if (labelA < labelB) return -1;
                    return 1;
                });
                let sortedPropList = pathList.map(prop => data.shProperty.find(sp => {
                    const spData = GS.data(sp, this.props.graph);
                    return spData.shPath === prop;
                }));
                let inverses = [];
                let advancedDomains = [];
                // We check if the target classes match up with property domains
                if (data.shTargetClass && data.shTargetClass.length > 0) {
                    pathList.forEach(sp => {
                        const isLoaded = GS.isLoaded(sp, associatedProjectGraph);
                        if (!isLoaded) return;
                        const pData = GS.data(sp, associatedProjectGraph);
                        const label = pData.label && pData.label[this.props.defaultLang] ?
                            pData.label[this.props.defaultLang]
                            :
                            pData.label && pData.label["und"] ?
                                pData.label["und"]
                                :
                                pData.id;
                        if ((pData.inverseOf && ((Array.isArray(pData.inverseOf) && pData.inverseOf.length > 0) || !Array.isArray(pData.inverseOf)))
                            ||
                            inverseList.includes(sp)) {
                            inverses.push(label);
                        }
                    });
                }
                data.shProperty.forEach(shProp => {
                    const isShapeLoaded = GS.isLoaded(shProp, this.props.graph);
                    if (!isShapeLoaded) return;
                    const shPropData = GS.data(shProp, this.props.graph);
                    const isLoaded = GS.isLoaded(shPropData.shPath, associatedProjectGraph);
                    if (!isLoaded) return;
                    const pData = GS.data(shPropData.shPath, associatedProjectGraph);
                    if (pData.domain && pData.domain.some(d => d.startsWith(BN_NAMESPACE))) {
                        advancedDomains.push(shProp);
                    }
                });
                this.setState({ isLoading: false, associatedProperty: pathList, sortedPropList, inverses, advancedDomains });
            }
            catch (e) {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, isLoading: false });
            }
        });
    }

    deletePropertyShape = (iri) => GS.deleteResource(iri, this.props.graph);

    onAddClass = (value) => {
        // Check not already added
        if (!this.props.data.shTargetClass || !this.props.data.shTargetClass.includes(value)) {
            this.props.onAdd("shTargetClass", value);
        }
    };

    onAddTargetPredicate = (value) => {
        // Check not already added
        if (!this.props.data.targetPredicate || !this.props.data.targetPredicate.includes(value)) {
            this.props.onAdd("targetPredicate", value);
        }
    };

    onUpdate = () => (value) => this.props.onUpdate("label", value, this.props.defaultLang);

    onAddPropertyShape = (value) => {
        let newAPs = this.state.associatedProperty ?
            [...this.state.associatedProperty]
            :
            [];
        // Just in case already added
        if (newAPs.includes(value)) return;
        newAPs.push(value);
        this.setState({ associatedProperty: newAPs }, async () => {
            const shapeIRI = "https://grafsync.graphifi.com/data/shape/" + uuidv4();
            let newShape = {
                id: shapeIRI,
                type: "PropertyShape",
                shPath: value
            };
            await GS.insertResource(newShape, shapeIRI, this.props.graph, [this.props.data.id]);
            this.props.onAdd("shProperty", shapeIRI);
        });
    };

    checkIfSure = () => this.setState({ areYouSure: true });

    onSure = () => {
        this.setState({ areYouSure: false });
        this.props.onDeleteTemplate(this.props.id);
    };

    handleTargetChange = (targetClass) => {
        if (this.props.data.shTargetClass && this.props.data.shTargetClass.includes(targetClass)) {
            this.props.onDelete("shTargetClass", targetClass);
        }
        else {
            this.onAddClass(targetClass);
        }
    };

    handleDisplayChange = () => {
        if (this.props.data.shGroup && this.props.data.shGroup.includes(NS_GS + "hiddenByDefault")) {
            this.props.onDelete("shGroup", NS_GS + "hiddenByDefault");
        }
        else {
            this.props.onAdd("shGroup", NS_GS + "hiddenByDefault");
        }
    };

    onNotSure = () => this.setState({ areYouSure: false });

    render() {

        const { classes, graph, data, id, project, defaultLang, ontologyProjectGraph, isReadOnly, skos, skosXL, conceptTarget,
            allowedClasses, ontologyProject, enableProjectSelection, dataset, modules, includeSelectFrom, globalProps } = this.props;
        const { associatedProperty, areYouSure, isErrored, sortedPropList, inverses, advancedDomains } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        const selectableClasses = allowedClasses ?
            allowedClasses.filter(ac => !data.shTargetClass || !data.shTargetClass.includes(ac))
            :
            [];
        let shaclProperties = data.shProperty ?
            sortedPropList
            :
            [];

        const usingUnavailableClasses = data.shTargetClass && data.shTargetClass
            .filter(tc => !STANDARD_TARGETS.includes(tc))
            .some(tc => !allowedClasses.includes(tc));

        const containsAdvancedDomains = shaclProperties && shaclProperties.some(ad => advancedDomains.includes(ad));
        const containsGlobalProps = associatedProperty && globalProps && associatedProperty.some(ad => globalProps.includes(ad));

        const templateTestName = data.label[defaultLang] ?
            data.label[defaultLang].replace(/[^a-zA-Z0-9]/g, "")
            :
            data.id;

        return (
            <div data-test={"template-" + templateTestName} ref={this.cardRef}>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginTop: '10px', marginBottom: 12 }}>
                    <div data-test="template-title-text" style={{ flex: 1 }}>
                        <Typography className={classes.subMainHeading} style={{ marginTop: 12 }}>{data.label[defaultLang]}</Typography>
                    </div>
                    {!isReadOnly && <Tooltip placement="right" title='Delete template' style={{ alignSelf: 'flex-end' }}>
                        <IconButton aria-label="Delete template" size="small" data-test="delete-template" name="deleteButton" onClick={this.checkIfSure} className={classes.deleteButton}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    }
                </div>
                <form autoComplete="off" noValidate="novalidate">
                    <BasicLanguageFieldDisplay
                        dataTest="template-title"
                        ariaLabel="Template title"
                        defaultLang={defaultLang}
                        title="Template Title"
                        property="label"
                        values={data.label}
                        onePerLang={true}
                        placeholder="Enter title text"
                        name="Template Title"
                        projectLang={[defaultLang]}
                        required={true}
                        maxLength={MAX_TITLE_LENGTH}
                        onUpdate={this.onUpdate}
                        onDelete={() => { }}
                        isReadOnly={isReadOnly} />
                </form>
                <div style={{ display: 'flex', marginTop: 12, width: '100%' }}>
                    <div style={{ flex: 1 }}>
                        <TitledPanel margin={false} dataTest="template-classes" height="full" title="Target Classes"
                            actions={
                                !isReadOnly && selectableClasses.length > 0 ?
                                    <SelectResourcesDialog
                                        title="Add Template Classes"
                                        buttonTitle='Add template classes'
                                        setHeight={false}
                                        resources={selectableClasses}
                                        addResource={this.onAddClass}
                                        graph={ontologyProjectGraph}
                                        defaultLang={defaultLang} />
                                    :
                                    null
                            }>
                            {usingUnavailableClasses &&
                                <div style={{ marginBottom: 12 }} data-test="unavailable-classes-warning">
                                    <Notification
                                        error={false}
                                        text="This template is using classes that are not currently available for a user to select."
                                        dataTest="class-not-available-warning" />
                                </div>
                            }
                            <div>
                                {data.shTargetClass && data.shTargetClass.length > 0 ?
                                    [...data.shTargetClass]
                                        .filter(tc => !STANDARD_TARGETS.includes(tc))
                                        .sort().map((ac, index) =>
                                            <CommonIriDisplay
                                                dataTest={"class-resource-link-" + index}
                                                key={ac}
                                                id={ac}
                                                projectIRI={ontologyProject}
                                                loadComponents={true}
                                                checkIfUsedOnDelete={
                                                    {
                                                        graph,
                                                        value: ac,
                                                        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                                                        message: [
                                                            "This associated class is used within this project.",
                                                            "Removing it from this template will not remove data using it.",
                                                            "Do you want to proceed with removing this class?"
                                                        ]
                                                    }
                                                }
                                                highlightNotAvailable={true}
                                                handleNotAvailable={true}
                                                isReadOnly={isReadOnly}
                                                sourceGraph={graph}
                                                wrap={false}
                                                graph={ontologyProjectGraph}
                                                property="shTargetClass"
                                                source={data.id}
                                                viewLang={defaultLang}
                                                defaultLang={defaultLang} />
                                        )
                                    :
                                    <Typography paragraph data-test="no-classes-selected" variant="body2">Select classes that you want this template to display for. Classes can only be chosen from those made available in the list above and from the checkboxes below.<br /><span style={{ fontWeight: 500 }} data-test="no-classes-selected" variant="body2">As no classes are selected this template will display in all places where it is used (except components or annotations).</span></Typography>
                                }
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                {conceptTarget &&
                                    <FormControlLabel
                                        data-test="option-concept"
                                        control={
                                            <Checkbox
                                                size="small"
                                                color="primary"
                                                disabled={isReadOnly}
                                                name="allowConcept"
                                                className={classes.checked}
                                                checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2004/02/skos/core#Concept") : false}
                                                onClick={() => this.handleTargetChange("http://www.w3.org/2004/02/skos/core#Concept")} />
                                        }
                                        label="Concepts"
                                        classes={{ label: classes.formLabelInline }}
                                    />
                                }
                                {skosXL &&
                                    <FormControlLabel
                                        data-test="option-label"
                                        control={
                                            <Checkbox
                                                size="small"
                                                color="primary"
                                                disabled={isReadOnly}
                                                name="allowXL"
                                                className={classes.checked}
                                                checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2008/05/skos-xl#Label") : false}
                                                onClick={() => this.handleTargetChange("http://www.w3.org/2008/05/skos-xl#Label")} />
                                        }
                                        label="SKOS-XL Labels"
                                        classes={{ label: classes.formLabelInline }}
                                    />
                                }
                                {dataset &&
                                    <FormControlLabel
                                        data-test="option-dataset"
                                        control={
                                            <Checkbox
                                                size="small"
                                                color="primary"
                                                disabled={isReadOnly}
                                                name="optionDataset"
                                                className={classes.checked}
                                                checked={data.shTargetClass ? data.shTargetClass.includes("https://grafsync.graphifi.com/schema/gs/Dataset") : false}
                                                onClick={() => this.handleTargetChange("https://grafsync.graphifi.com/schema/gs/Dataset")} />
                                        }
                                        label="Datasets"
                                        classes={{ label: classes.formLabelInline }}
                                    />
                                }
                                {skos &&
                                    <React.Fragment>
                                        <FormControlLabel
                                            data-test="option-taxonomy"
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    disabled={isReadOnly}
                                                    name="optionTaxonomy"
                                                    className={classes.checked}
                                                    checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme") : false}
                                                    onClick={() => this.handleTargetChange("http://www.w3.org/2004/02/skos/core#ConceptScheme")} />
                                            }
                                            label="Taxonomies"
                                            classes={{ label: classes.formLabelInline }}
                                        />
                                        <FormControlLabel
                                            data-test="option-collections"
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    disabled={isReadOnly}
                                                    name="optionCollections"
                                                    className={classes.checked}
                                                    checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2004/02/skos/core#Collection") : false}
                                                    onClick={() => this.handleTargetChange("http://www.w3.org/2004/02/skos/core#Collection")} />
                                            }
                                            label="Collections"
                                            classes={{ label: classes.formLabelInline }}
                                        />
                                        <FormControlLabel
                                            data-test="option-ordered-collections"
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    disabled={isReadOnly}
                                                    name="optionOrderedCollections"
                                                    className={classes.checked}
                                                    checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection") : false}
                                                    onClick={() => this.handleTargetChange("http://www.w3.org/2004/02/skos/core#OrderedCollection")} />
                                            }
                                            label="Ordered Collections"
                                            classes={{ label: classes.formLabelInline }}
                                        />
                                        <FormControlLabel
                                            data-test="option-notes"
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    disabled={isReadOnly}
                                                    name="optionNotes"
                                                    className={classes.checked}
                                                    checked={data.shTargetClass ? data.shTargetClass.includes("https://graphifi.com/schema/xkos/Note") : false}
                                                    onClick={() => this.handleTargetChange("https://graphifi.com/schema/xkos/Note")} />
                                            }
                                            label="Notes"
                                            classes={{ label: classes.formLabelInline }}
                                        />
                                    </React.Fragment>
                                }
                                <FormControlLabel
                                    data-test="option-statement"
                                    control={
                                        <Checkbox
                                            size="small"
                                            color="primary"
                                            disabled={isReadOnly}
                                            name="optionStatement"
                                            className={classes.checked}
                                            checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement") : false}
                                            onClick={() => this.handleTargetChange("http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement")} />
                                    }
                                    label="Annotations"
                                    classes={{ label: classes.formLabelInline }}
                                />
                            </div>
                        </TitledPanel>
                    </div>
                    <div style={{ marginLeft: 12 }}>
                        <TitledPanel margin={false} dataTest="template-visibility" height="full" title="Options">
                            <div>
                                <LabelledSwitch
                                    dataTest="hide-template"
                                    disabled={isReadOnly}
                                    checked={data.shGroup && data.shGroup.includes(NS_GS + "hiddenByDefault")}
                                    handleChange={this.handleDisplayChange}
                                    label="Hidden By Default" />
                            </div>
                        </TitledPanel>
                    </div>
                </div>
                {data.shTargetClass && data.shTargetClass.includes("http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement") &&
                    <div style={{ marginTop: 12 }}>
                        <TitledPanel margin={false} dataTest="template-target-predicates" height="full" title="Target Properties (for Annotation)" actions={
                            !isReadOnly ?
                                <FindPropertyDialog
                                    defaultLang={defaultLang}
                                    projects={[ontologyProject]}
                                    exclude={data.targetPredicate}
                                    onAddProperty={this.onAddTargetPredicate} />
                                :
                                null
                        }>
                            <div>
                                {data.targetPredicate && data.targetPredicate.length > 0 ?
                                    [...data.targetPredicate]
                                        .sort().map((ac, index) =>
                                            <CommonIriDisplay
                                                dataTest={"predicate-resource-link-" + index}
                                                key={ac}
                                                id={ac}
                                                projectIRI={ontologyProject}
                                                loadComponents={true}
                                                handleNotAvailable={true}
                                                isReadOnly={isReadOnly}
                                                sourceGraph={graph}
                                                wrap={false}
                                                graph={ontologyProjectGraph}
                                                property="targetPredicate"
                                                source={data.id}
                                                viewLang={defaultLang}
                                                defaultLang={defaultLang} />
                                        )
                                    :
                                    <Typography paragraph data-test="no-target-predicates-selected" variant="body2">Select properties that you want to annotate with this template.</Typography>
                                }
                            </div>
                        </TitledPanel>
                    </div>
                }
                {data.shTargetClass && (
                    data.shTargetClass.includes("http://www.w3.org/1999/02/22-rdf-syntax-ns#Statement") ||
                    data.shTargetClass.includes("https://graphifi.com/schema/xkos/Note"))
                    && inverses.length > 0 &&
                    <div style={{ marginTop: 12, marginBottom: 6 }} data-test="inverse-warning">
                        <Notification
                            error={false}
                            text={"This template has properties that are symmetric or that have inverses. These will be suppressed for SKOS notes and annotations. The properties are: "
                                + inverses.sort().join(", ")}
                            dataTest="inverse-issue-warning" />
                    </div>
                }
                {containsAdvancedDomains &&
                    <div style={{ marginTop: 12, marginBottom: 6 }} data-test="advanced-domains-warning">
                        <Notification
                            error={false}
                            text="This template is using properties with complex domain constructs. These constructs will be ignored."
                            dataTest="domain-warning" />
                    </div>
                }
                {containsGlobalProps &&
                    <div style={{ marginTop: 12, marginBottom: 6 }} data-test="global-props-warning">
                        <Notification
                            error={false}
                            text="This template is using properties that are also global properties. These properties will be ignored as global properties take precedence."
                            dataTest="props-warning" />
                    </div>
                }
                <div style={{ marginTop: 12 }}>
                    <TitledPanel margin={false} dataTest="template-properties" height="full" title="Properties to Display" actions={
                        !isReadOnly ?
                            <FindPropertyDialog
                                defaultLang={defaultLang}
                                projects={[ontologyProject]}
                                exclude={associatedProperty}
                                excludeByPath={[
                                    {
                                        predicate: [
                                            "http://www.w3.org/ns/shacl#property",
                                            "http://www.w3.org/ns/shacl#path"
                                        ],
                                        target: id,
                                        targetGraph: graph,
                                        reverse: true
                                    }
                                ]}
                                onAddProperty={this.onAddPropertyShape} />
                            :
                            null
                    }>
                        {shaclProperties && shaclProperties.length > 0 &&
                            <div style={{ overflowX: 'scroll', overflowY: 'hidden', width: '100%' }}>
                                <Table style={{ width: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell style={{ minWidth: 60, verticalAlign: 'bottom', paddingLeft: 0 }}>
                                                <Typography variant="body2">Name Override</Typography>
                                            </TableCell>
                                            <TableCell style={{ minWidth: 60, verticalAlign: 'bottom', paddingLeft: 0 }}>
                                                <Typography variant="body2">Description</Typography>
                                            </TableCell>
                                            <TableCell style={{ minWidth: 90, verticalAlign: 'bottom', paddingLeft: 0 }}>
                                                <Typography variant="body2">Min.<br />Count</Typography>
                                            </TableCell>
                                            <TableCell style={{ minWidth: 90, verticalAlign: 'bottom', paddingLeft: 0 }}>
                                                <Typography variant="body2">Max.<br />Count</Typography>
                                            </TableCell>
                                            <TableCell style={{ minWidth: 90, verticalAlign: 'bottom', paddingLeft: 0 }}>
                                                <Typography variant="body2">Max.<br />Length</Typography>
                                            </TableCell>
                                            <TableCell style={{ minWidth: 100, verticalAlign: 'bottom', paddingLeft: 0 }}>
                                                <Typography variant="body2">Pattern</Typography>
                                            </TableCell>
                                            <TableCell style={{ paddingLeft: 0 }}>
                                                <Typography variant="body2">Treat<br />Content As</Typography>
                                            </TableCell>
                                            {includeSelectFrom &&
                                                <TableCell style={{ paddingLeft: 0 }}>
                                                    <Typography variant="body2">Select From</Typography>
                                                </TableCell>
                                            }
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {shaclProperties.map((ap, index) =>
                                            <ShaclProperty
                                                dataTest={"prop-item-" + index}
                                                key={ap}
                                                id={ap}
                                                modules={modules}
                                                suppressLoading={true}
                                                includeSelectFrom={includeSelectFrom}
                                                enableProjectSelection={enableProjectSelection}
                                                onDeleteLink={this.deletePropertyShape}
                                                handleNotAvailable={true}
                                                isReadOnly={isReadOnly}
                                                graph={graph}
                                                project={project}
                                                ontologyProjectGraph={ontologyProjectGraph}
                                                defaultLang={defaultLang} />
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        }
                        {(!shaclProperties || shaclProperties.length === 0) &&
                            <React.Fragment>
                                <Typography paragraph data-test="no-properties-selected" variant="body2">Select properties that you want to be in this template.</Typography>
                            </React.Fragment>
                        }
                    </TitledPanel>
                </div>
                {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message="Do you really want to delete this template?" />}
            </div>
        );
    }
}

TemplateManager.defaultProps = {
    isReadOnly: false,
    skos: false,
    skosXL: false,
    dataset: false,
    enableProjectSelection: false,
    conceptTarget: false,
    includeSelectFrom: true
};

TemplateManager.propTypes = {
    classes: PropTypes.object,
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
    displayTemplateIRI: PropTypes.string,
    allowedClasses: PropTypes.array,
    globalProps: PropTypes.array,
    onAdd: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    project: PropTypes.string.isRequired,
    dataset: PropTypes.bool,
    skos: PropTypes.bool,
    skosXL: PropTypes.bool,
    modules: PropTypes.number,
    enableProjectSelection: PropTypes.bool,
    includeSelectFrom: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    onDeleteTemplate: PropTypes.func.isRequired,
    ontologyProject: PropTypes.string.isRequired,
    ontologyProjectGraph: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired
};

export default withStyles(styles)(withGS(TemplateManager));